import React, { useState, useEffect, useContext } from 'react';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Download, Mail, RefreshCw, Check, X, Trash2 } from 'lucide-react';
import TemplateManager from './TemplateManager';

function QuoteManagement() {
	const { hasPermission } = useAuth();
    const { user, isAdmin } = useAuth();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [showActionModal, setShowActionModal] = useState(false);
    const [actionType, setActionType] = useState('');
    const [discount, setDiscount] = useState(0);
    const [selectedQuotes, setSelectedQuotes] = useState([]);
    const [approvalTerms, setApprovalTerms] = useState('');
    const [approvalSignature, setApprovalSignature] = useState('');
    const [approvalBankDetails, setApprovalBankDetails] = useState('');
    const [showTemplateManager, setShowTemplateManager] = useState(false);

    useEffect(() => {
        fetchQuotes();
    }, []);

    const fetchQuotes = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get(`/quotes`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setQuotes(response.data);
        } catch (error) {
            console.error('Error fetching quotes:', error);
        } finally {
            setLoading(false);
        }
    };

    const downloadPDF = async (quoteId, retryCount = 0) => {
		try {
			const response = await apiClient.get(`/quotes/${quoteId}/pdf`, {
				responseType: 'blob',
				headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
			});
			
			// Check if we got a valid PDF
			if (response.data.size === 0) {
				throw new Error('Empty PDF response');
			}
			
			const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `quote_${quoteId}.pdf`);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
			
		} catch (error) {
			console.error('Error downloading PDF:', error);
			
			if (error.response?.status === 404 && retryCount < 2) {
				// Wait and retry - the backend will attempt to regenerate
				await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1)));
				return downloadPDF(quoteId, retryCount + 1);
			} else {
				alert('PDF is not available for this quote yet. Please try again in a moment or contact support if the issue persists.');
			}
		}
	};

    const openActionModal = (quote, action) => {
        setSelectedQuote(quote);
        setActionType(action);
        setDiscount(quote.discount || 0);
        setApprovalTerms(quote.terms_conditions || '');
        setApprovalSignature(quote.signature || '');
        setApprovalBankDetails(quote.bank_details || '');
        setShowActionModal(true);
    };

    const handleSelectQuote = (quoteId, checked) => {
        if (checked) {
            setSelectedQuotes([...selectedQuotes, quoteId]);
        } else {
            setSelectedQuotes(selectedQuotes.filter(id => id !== quoteId));
        }
    };

    const handleQuoteAction = async () => {
        if (!selectedQuote) return;
        
        setLoading(true);
        try {
            if (actionType === 'send') {
                // Use the dedicated send endpoint
                const response = await apiClient.post(
                    `/quotes/${selectedQuote.quote_id}/send`,
                    {},
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                );
                
                alert(response.data.message);
                fetchQuotes();
                setShowActionModal(false);
                return;
            } else if (actionType === 'approve') {
                // Use the action endpoint for approval with discount
                const response = await apiClient.post(
                    `/quotes/${selectedQuote.quote_id}/action`,
                    {
                        action: 'approve',
                        discount: discount
                    },
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                );
                
                alert(response.data.message);
                fetchQuotes();
                setShowActionModal(false);
                return;
            } else if (actionType === 'reject') {
                // Use the action endpoint for rejection
                const response = await apiClient.post(
                    `/quotes/${selectedQuote.quote_id}/action`,
                    {
                        action: 'reject'
                    },
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                );
                
                alert(response.data.message);
                fetchQuotes();
                setShowActionModal(false);
                return;
            }
        } catch (error) {
            console.error('Error performing quote action:', error);
            alert('Failed to perform action. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteQuote = async (quoteId) => {
        if (!window.confirm('Are you sure you want to delete this quote?')) return;
        
        try {
            await apiClient.delete(`/quotes/${quoteId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            fetchQuotes();
        } catch (error) {
            console.error('Error deleting quote:', error);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedQuotes.length === 0 || !window.confirm('Delete selected quotes?')) return;
        try {
            const response = await apiClient.delete(`/quotes/bulk/delete`, {
                data: { quote_ids: selectedQuotes },
                headers: { 
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}` 
                }
            });
            
            alert(response.data.message);
            fetchQuotes();
            setSelectedQuotes([]);
        } catch (error) {
            console.error('Error bulk deleting:', error);
            alert('Failed to delete quotes. Please try again.');
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
            case 'approved':
                return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
            case 'rejected':
                return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
            case 'sent':
                return <Badge className="bg-blue-100 text-blue-800">Sent</Badge>;
            default:
                return <Badge>{status}</Badge>;
        }
    };

    // Calculate quote summary for display
    const calculateQuoteSummary = (quote) => {
        const itemsTotal = quote.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        const discountAmount = quote.discount > 0 ? itemsTotal * (quote.discount / 100) : 0;
        const subtotalAfterDiscount = itemsTotal - discountAmount;
        const totalBeforeTax = subtotalAfterDiscount + quote.pf_costs;
        const taxAmount = totalBeforeTax * (quote.gst_percentage / 100);
        const totalAfterTax = totalBeforeTax + taxAmount;

        return {
            itemsTotal,
            discountAmount,
            subtotalAfterDiscount,
            totalBeforeTax,
            taxAmount,
            totalAfterTax
        };
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex justify-between items-center">
                    <CardTitle>Quote Management</CardTitle>
                    <Button onClick={fetchQuotes} disabled={loading}>
                        <RefreshCw className="mr-2" size={16} />
                        {loading ? 'Refreshing...' : 'Refresh Quotes'}
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>
                                <Checkbox 
                                    checked={selectedQuotes.length === quotes.length && quotes.length > 0}
                                    onCheckedChange={(checked) => {
                                        if (checked) {
                                            setSelectedQuotes(quotes.map(quote => quote.quote_id));
                                        } else {
                                            setSelectedQuotes([]);
                                        }
                                    }} 
                                />
                            </TableHead>
                            <TableHead>Quote ID</TableHead>
                            <TableHead>Client</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Total</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {quotes.map(quote => {
                            const summary = calculateQuoteSummary(quote);
                            
                            return (
                                <TableRow key={quote.quote_id}>
                                    <TableCell>
                                        <Checkbox 
                                            checked={selectedQuotes.includes(quote.quote_id)} 
                                            onCheckedChange={(checked) => handleSelectQuote(quote.quote_id, checked)} 
                                        />
                                    </TableCell>
                                    <TableCell className="font-medium">{quote.quote_id}</TableCell>
                                    <TableCell>{quote.company_name}</TableCell>
                                    <TableCell>{quote.quote_type}</TableCell>
                                    <TableCell className="font-semibold">
                                        ₹{summary.totalAfterTax.toFixed(2)}
                                        {quote.discount > 0 && (
                                            <span className="text-green-600 text-xs block">({quote.discount}% discount applied)</span>
                                        )}
                                    </TableCell>
                                    <TableCell>{getStatusBadge(quote.status)}</TableCell>
                                    <TableCell>{new Date(quote.created_at).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        <div className="flex space-x-2">
                                            <Button 
                                                size="icon" 
                                                variant="outline"
                                                onClick={() => downloadPDF(quote.quote_id)}
                                                title="Download PDF"
                                            >
                                                <Download size={16} />
                                            </Button>
                                            
											{quote.status === 'pending' && hasPermission('approve_quotes') && (
											    <>
											  <Button 
												size="icon" 
												variant="success"
												onClick={() => openActionModal(quote, 'approve')}
												title="Approve Quote"
											  >
												<Check size={16} />
											  </Button>
											
                                                    <Button 
                                                        size="icon" 
                                                        variant="destructive"
                                                        onClick={() => openActionModal(quote, 'reject')}
                                                        title="Reject Quote"
                                                    >
                                                        <X size={16} />
                                                    </Button>
                                                </>
                                            )}
                                            
											{quote.status === 'approved' && hasPermission('send_quotes') && (
											  <Button 
												size="icon" 
												variant="primary"
												onClick={() => openActionModal(quote, 'send')}
												title="Send to Client"
											  >
												<Mail size={16} />
											  </Button>
											)}
                                            
                                            <Button 
                                                size="icon" 
                                                variant="destructive"
                                                onClick={() => handleDeleteQuote(quote.quote_id)}
                                                title="Delete Quote"
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {selectedQuotes.length > 0 && (
                    <Button variant="destructive" onClick={handleBulkDelete} className="mt-4">
                        Bulk Delete Selected
                    </Button>
                )}
            </CardContent>

            {/* Action Modal - FIXED SCROLLING ISSUE */}
            <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
                <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
                    <div className="flex-shrink-0">
                        <DialogHeader>
                            <DialogTitle>
                                {actionType === 'approve' && 'Approve Quote'}
                                {actionType === 'reject' && 'Reject Quote'}
                                {actionType === 'send' && 'Send Quote to Client'}
                            </DialogTitle>
                        </DialogHeader>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto space-y-4 min-h-0">
                        {actionType === 'approve' && (
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="discount">Apply Discount (%)</Label>
                                    <Input
                                        id="discount"
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={discount}
                                        onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                                    />
                                </div>
                                
                                {/* Show preview of calculations when discount is applied */}
                                {discount > 0 && selectedQuote && (
                                    <div className="border rounded-lg p-4 bg-gray-50">
                                        <h4 className="font-semibold mb-2">Price Preview</h4>
                                        <div className="space-y-1 text-sm">
                                            <div className="flex justify-between">
                                                <span>Items Total:</span>
                                                <span>₹{selectedQuote.items_total.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Discount ({discount}%):</span>
                                                <span>-₹{(selectedQuote.items_total * (discount / 100)).toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between border-t pt-1">
                                                <span>Subtotal:</span>
                                                <span>₹{(selectedQuote.items_total - (selectedQuote.items_total * (discount / 100))).toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>P&F Costs:</span>
                                                <span>₹{selectedQuote.pf_costs.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Total Before Tax:</span>
                                                <span>₹{(selectedQuote.items_total - (selectedQuote.items_total * (discount / 100)) + selectedQuote.pf_costs).toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>GST ({selectedQuote.gst_percentage}%):</span>
                                                <span>₹{((selectedQuote.items_total - (selectedQuote.items_total * (discount / 100)) + selectedQuote.pf_costs) * (selectedQuote.gst_percentage / 100)).toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between font-semibold border-t pt-1">
                                                <span>Total After Tax:</span>
                                                <span>₹{((selectedQuote.items_total - (selectedQuote.items_total * (discount / 100)) + selectedQuote.pf_costs) * (1 + selectedQuote.gst_percentage / 100)).toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                <p className="text-sm text-gray-600">
                                    This will approve the quote and apply the discount. The client will not be notified yet.
                                </p>
                            </div>
                        )}
                        
                        {actionType === 'approve' && isAdmin && (
                            <>
                                <Button 
                                    type="button" 
                                    onClick={() => setShowTemplateManager(true)}
                                    className="mb-4"
                                >
                                    Load from Template
                                </Button>
                                
                                <div>
                                    <Label htmlFor="approval_terms">Terms & Conditions</Label>
                                    <Textarea
                                        id="approval_terms"
                                        value={approvalTerms}
                                        onChange={(e) => setApprovalTerms(e.target.value)}
                                        rows={4}
                                        placeholder="Enter terms and conditions for this quote..."
                                    />
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="border rounded-lg p-3">
                                        <Label htmlFor="approval_signature" className="block mb-2 font-semibold">Signature Area</Label>
                                        <Textarea
                                            id="approval_signature"
                                            value={approvalSignature}
                                            onChange={(e) => setApprovalSignature(e.target.value)}
                                            rows={2}
                                            placeholder="Enter signature details..."
                                            className="border-0 focus:ring-0"
                                        />
                                    </div>
                                    
                                    <div className="border rounded-lg p-3">
                                        <Label htmlFor="approval_bank_details" className="block mb-2 font-semibold">Bank Details</Label>
                                        <Textarea
                                            id="approval_bank_details"
                                            value={approvalBankDetails}
                                            onChange={(e) => setApprovalBankDetails(e.target.value)}
                                            rows={2}
                                            placeholder="Enter bank account details..."
                                            className="border-0 focus:ring-0"
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        
                        {actionType === 'reject' && (
                            <p className="text-gray-600">
                                Are you sure you want to reject this quote? This action cannot be undone.
                            </p>
                        )}
                        
                        {actionType === 'send' && (
                            <p className="text-gray-600">
                                This will send the approved quote to the client's email address. Are you ready to send?
                            </p>
                        )}
                    </div>
                    
                    {/* Fixed button section that stays at bottom */}
                    <div className="flex-shrink-0 flex justify-end space-x-4 pt-4 border-t">
                        <Button 
                            onClick={handleQuoteAction}
                            disabled={loading}
                            variant={
                                actionType === 'approve' ? 'success' : 
                                actionType === 'reject' ? 'destructive' : 'primary'
                            }
                        >
                            {loading ? 'Processing...' : 'Confirm'}
                        </Button>
                        <Button variant="outline" onClick={() => setShowActionModal(false)}>
                            Cancel
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {isAdmin && (
                <TemplateManager 
                    isOpen={showTemplateManager} 
                    onOpenChange={setShowTemplateManager}
                    onSelectTemplate={(template) => {
                        setApprovalTerms(template.terms_conditions);
                        setApprovalSignature(template.signature);
                        setApprovalBankDetails(template.bank_details);
                        setShowTemplateManager(false);
                    }}
                />
            )}
        </Card>
    );
}

export default QuoteManagement;